import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line camelcase
import { use_LoginInfoQuery, useUpdateStatusMutation } from 'autogen/swagger/all/hooks';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import useLoginInfoQuickView from 'components/login/shared/hooks/useLoginInfoQuickView';
import toast from 'components/Toast';
import isClient from 'helpers/isClient';
import { refreshFavoriteQuantityGlobally } from 'hooks/user/useFavoriteQuantity';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import mediator from 'modules/mediator';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import type { TrackingCategory } from 'modules/tracking/UAEventTracking';

/**
 * Handles the logic and state for favoriting an item
 * @param {boolean} initialFavoriteState - The initial state of whether the item is favorite
 */
export const useFavoriteItem = (initialFavoriteState: boolean, productId: number) => {
    const [isItemFavorite, setIsItemFavorite] = useState(initialFavoriteState);
    const isAuthenticated = useIsAuthenticated();
    const [, toggle] = useLoginInfoQuickView();
    const GA4Tracking = useGA4Tracking();
    const { t } = useTranslation();

    const { mutateAsync: updateStatus } = useUpdateStatusMutation<unknown, { uaTrackingCategory?: TrackingCategory }>({
        onSuccess: (data, { uaTrackingCategory }) => {
            refreshFavoriteQuantityGlobally();

            if (uaTrackingCategory) {
                GA4Tracking(({ eventCategoryToContext }) => ({
                    eventName: 'add_to_favorite',
                    context: eventCategoryToContext(uaTrackingCategory),
                    category: 'ecommerce'
                }));
            }
        }
    });
    const { refetch: getLoginInfo } = use_LoginInfoQuery(
        {
            productId,
            infoType: 'AddFavorite',
            level: 'Soft',
            mode: 'Favorite',
            returnUrl: isClient ? window.location.pathname : '/'
        },
        { enabled: false }
    );

    /**
     * Optimistic update helper method
     * @param favorite
     * @returns
     */
    const optimisticUpdate = (favorite) => {
        setIsItemFavorite(favorite);

        const update = {
            revert: () => {
                setIsItemFavorite(!favorite);
            }
        };
        return update;
    };

    /**
     * Handles opening the login modal when user is not authenticated and unable to favorite.
     * @param uaTrackingCategory
     */
    async function handleOpenLoginInfo(uaTrackingCategory?: TrackingCategory) {
        let outcome;

        try {
            const { data: loginInfo } = await getLoginInfo();

            toggle({ isOpen: true, loginInfo });

            mediator.publish('closeModal');

            outcome = 'success';
        } catch (error) {
            toast.error(t(GeneralExceptionError));

            console.error(error);

            outcome = 'error';
        }

        if (uaTrackingCategory) {
            GA4Tracking(({ eventCategoryToContext }) => ({
                eventName: 'open_sso_modal',
                context: eventCategoryToContext(uaTrackingCategory),
                category: 'login',
                eventParams: [
                    { param_name: 'outcome', param_value: outcome },
                    { param_name: 'click_type', param_value: 'favorite' }
                ]
            }));
        }
    }

    /**
     * Handles updating the favorite status of a product item & optimistic update
     * @param uaTrackingCategory
     */
    async function handleUpdateItemStatus(uaTrackingCategory?: TrackingCategory) {
        const update = optimisticUpdate(!isItemFavorite);
        try {
            const params = {
                productId,
                isFavorite: !isItemFavorite
            };

            const response = await updateStatus({ params, uaTrackingCategory });

            GA4Tracking({
                eventName: 'add_to_favorite',
                category: 'ecommerce',
                context: 'none',
                eventParams: [{ param_name: 'toggle', param_value: params.isFavorite }]
            });

            if (!response.success) throw new Error();
        } catch (error) {
            if (update) update.revert();
            toast.error(t(GeneralExceptionError));
            console.error(error);
        }
    }

    const handleFavoriteItem = (uaTrackingCategory?: TrackingCategory) => {
        if (!isAuthenticated) {
            handleOpenLoginInfo(uaTrackingCategory);
        } else {
            handleUpdateItemStatus(uaTrackingCategory);
        }
    };

    return { isItemFavorite, handleFavoriteItem };
};

export default useFavoriteItem;
